import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { dlGtpDataVolumeCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the DlGtpDataVolumeCell component
 */
interface DlGtpDataVolumeCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
}

/**
 * Downlink GTP Data Volume Chart (LTE Cell Level)
 * 
 * Displays downlink GPRS Tunneling Protocol (GTP) data volume metrics for individual LTE cells.
 * Shows the total volume of downlink data transmitted through the cell over time.
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level DL data volume metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <DlGtpDataVolumeCell
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (dlGtpDataVolumeCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/dl-gtp-data-volume?cell_name={cellName}
 */

const DlGtpDataVolumeCell: React.FC<DlGtpDataVolumeCellProps> = ({ 
  onFullscreenToggle, 
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={dlGtpDataVolumeCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default DlGtpDataVolumeCell;
